import { RawSourceMap, SourceMapConsumer } from "source-map-js";
import { PERKS } from "./game/data/perks";
import { SKILLS } from "./game/data/skills";
import { deterministicPolicy } from "./game/state/multiEncounterSimulation";
import {
  MultiEncounterRun,
  simulateMultiEncounterRun,
} from "./game/state/multiEncounterSimulation";

type LogLevel = "log" | "warn" | "error";

interface DebugConsoleOverlay {
  collapse(): void;
  expand(): void;
  toggle(): void;
  clear(): void;
  destroy(): void;
}

interface DebugConsoleOverlayOptions {
  collapsed?: boolean;
  maxEntries?: number;
}

interface LogEntry {
  level: LogLevel;
  message: string;
}

const DEFAULT_MAX_ENTRIES = 200;
const AUTO_SCROLL_THRESHOLD = 24;
const STACK_FRAME_REGEX = /(https?:\/\/[^\s)]+\.js):(\d+):(\d+)/;

const MULTI_ENCOUNTER_PRESET = {
  floors: 6,
  maxTurns: 20,
  seeds: [1, 2, 3],
};

let activeOverlay: DebugConsoleOverlay | null = null;
const sourceMapConsumers = new Map<string, Promise<SourceMapConsumer | null>>();

async function getSourceMapConsumer(url: string): Promise<SourceMapConsumer | null> {
  if (sourceMapConsumers.has(url)) {
    return sourceMapConsumers.get(url)!;
  }

  const consumerPromise = fetch(`${url}.map`)
    .then((response) => (response.ok ? response.json() : null))
    .then((rawMap) => (rawMap ? new SourceMapConsumer(rawMap as RawSourceMap) : null))
    .catch(() => null);

  sourceMapConsumers.set(url, consumerPromise);
  return consumerPromise;
}

async function mapStackTrace(stack?: string | null): Promise<string | null> {
  if (!stack) {
    return null;
  }

  const lines = stack.split("\n");
  const mappedLines = await Promise.all(
    lines.map(async (line) => {
      const match = line.match(STACK_FRAME_REGEX);
      if (!match) {
        return line.trim();
      }

      const [, fileUrl, lineNumber, columnNumber] = match;
      const consumer = await getSourceMapConsumer(fileUrl);
      if (!consumer) {
        return line.trim();
      }

      const position = consumer.originalPositionFor({
        line: Number(lineNumber),
        column: Number(columnNumber),
      });

      if (!position.source || position.line == null) {
        return line.trim();
      }

      const mappedLocation = `${position.source}:${position.line}:${position.column ?? 0}`;
      return line.replace(match[0], mappedLocation).trim();
    })
  );

  return mappedLines.join("\n");
}

export function createDebugConsoleOverlay(
  options: DebugConsoleOverlayOptions = {}
): DebugConsoleOverlay {
  if (activeOverlay) {
    return activeOverlay;
  }

  const maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES;
  let collapsed = options.collapsed ?? true;

  const originalConsole: Record<LogLevel, Console["log"]> = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };

  // Filter function to suppress noisy logs
  const shouldSuppressLog = (args: unknown[]): boolean => {
    const message = args.map((arg) => String(arg)).join(" ");

    // Suppress joystick library event warnings
    if (/Event (addedtoscene|removedfromscene) not supported/.test(message)) {
      return true;
    }

    return false;
  };

  const overlayRoot = document.createElement("div");
  const panel = document.createElement("div");
  const header = document.createElement("div");
  const title = document.createElement("span");
  const controls = document.createElement("div");
  const toggleButton = document.createElement("button");
  const copyButton = document.createElement("button");
  const clearButton = document.createElement("button");
  const balanceButton = document.createElement("button");
  const collapsedButton = document.createElement("button");
  const logContainer = document.createElement("div");
  const modalBackdrop = document.createElement("div");
  const modal = document.createElement("div");
  const modalHeader = document.createElement("div");
  const modalTitle = document.createElement("span");
  const modalClose = document.createElement("button");
  const modalBody = document.createElement("div");
  const entries: HTMLElement[] = [];
  const logMessages: string[] = [];

  overlayRoot.style.position = "fixed";
  overlayRoot.style.right = "12px";
  overlayRoot.style.bottom = "12px";
  overlayRoot.style.width = "min(520px, 92vw)";
  overlayRoot.style.zIndex = "9999";
  overlayRoot.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  overlayRoot.style.color = "#f0f0f0";
  overlayRoot.style.pointerEvents = "auto";
  overlayRoot.style.display = "flex";
  overlayRoot.style.flexDirection = "column";
  overlayRoot.style.alignItems = "flex-end";
  overlayRoot.style.gap = "8px";

  modalBackdrop.style.position = "fixed";
  modalBackdrop.style.inset = "0";
  modalBackdrop.style.background = "rgba(0, 0, 0, 0.6)";
  modalBackdrop.style.display = "none";
  modalBackdrop.style.alignItems = "center";
  modalBackdrop.style.justifyContent = "center";
  modalBackdrop.style.zIndex = "10000";
  modalBackdrop.style.padding = "12px";
  modalBackdrop.style.boxSizing = "border-box";

  modal.style.width = "min(520px, 94vw)";
  modal.style.maxHeight = "80vh";
  modal.style.background = "rgba(8, 8, 8, 0.94)";
  modal.style.border = "1px solid rgba(255, 255, 255, 0.12)";
  modal.style.borderRadius = "12px";
  modal.style.boxShadow = "0 10px 28px rgba(0, 0, 0, 0.45)";
  modal.style.display = "flex";
  modal.style.flexDirection = "column";
  modal.style.overflow = "hidden";

  modalHeader.style.display = "flex";
  modalHeader.style.alignItems = "center";
  modalHeader.style.justifyContent = "space-between";
  modalHeader.style.padding = "12px 16px";
  modalHeader.style.background = "rgba(20, 20, 20, 0.9)";
  modalHeader.style.borderBottom = "1px solid rgba(255, 255, 255, 0.08)";

  modalTitle.textContent = "Multi-Encounter Simulation";
  modalTitle.style.fontWeight = "600";
  modalTitle.style.fontSize = "14px";

  modalClose.textContent = "Close";
  modalClose.style.background = "rgba(255, 255, 255, 0.1)";
  modalClose.style.color = "#f0f0f0";
  modalClose.style.border = "1px solid rgba(255, 255, 255, 0.15)";
  modalClose.style.borderRadius = "6px";
  modalClose.style.padding = "6px 12px";
  modalClose.style.cursor = "pointer";
  modalClose.style.fontSize = "12px";

  modalBody.style.padding = "12px 16px 16px";
  modalBody.style.overflowY = "auto";
  modalBody.style.fontSize = "12px";
  modalBody.style.lineHeight = "1.5";

  panel.style.boxSizing = "border-box";
  panel.style.width = "100%";
  panel.style.maxHeight = "40vh";
  panel.style.background = "rgba(8, 8, 8, 0.82)";
  panel.style.backdropFilter = "blur(4px)";
  panel.style.boxShadow = "0 -4px 12px rgba(0, 0, 0, 0.4)";
  panel.style.border = "1px solid rgba(255, 255, 255, 0.08)";
  panel.style.borderRadius = "12px";
  panel.style.display = "flex";
  panel.style.flexDirection = "column";
  panel.style.gap = "4px";
  panel.style.overflow = "hidden";

  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.justifyContent = "space-between";
  header.style.padding = "8px 12px";
  header.style.cursor = "pointer";
  header.style.background = "rgba(20, 20, 20, 0.9)";
  header.style.userSelect = "none";

  title.textContent = "Debug Console";
  title.style.fontWeight = "600";
  title.style.fontSize = "14px";

  controls.style.display = "flex";
  controls.style.alignItems = "center";
  controls.style.gap = "8px";

  const baseButtonStyles = (button: HTMLButtonElement) => {
    button.style.background = "rgba(255, 255, 255, 0.1)";
    button.style.color = "#f0f0f0";
    button.style.border = "1px solid rgba(255, 255, 255, 0.15)";
    button.style.borderRadius = "6px";
    button.style.padding = "4px 10px";
    button.style.fontSize = "12px";
    button.style.cursor = "pointer";
    button.style.touchAction = "manipulation";
  };

  toggleButton.textContent = collapsed ? "Expand" : "Collapse";
  baseButtonStyles(toggleButton);

  copyButton.textContent = "Copy";
  baseButtonStyles(copyButton);

  clearButton.textContent = "Clear";
  baseButtonStyles(clearButton);

  balanceButton.textContent = "Balance";
  baseButtonStyles(balanceButton);

  controls.append(toggleButton, copyButton, clearButton, balanceButton);
  header.append(title, controls);

  logContainer.style.display = collapsed ? "none" : "block";
  logContainer.style.flex = "1";
  logContainer.style.overflowY = "auto";
  logContainer.style.padding = "4px 12px 12px";
  logContainer.style.boxSizing = "border-box";
  logContainer.style.fontSize = "12px";
  logContainer.style.lineHeight = "1.4";
  logContainer.style.maxHeight = "40vh";
  logContainer.style.whiteSpace = "pre-wrap";
  logContainer.style.wordBreak = "break-word";

  // Create SVG bug icon matching primitive icon style
  const bugIconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  bugIconSvg.setAttribute("width", "24");
  bugIconSvg.setAttribute("height", "24");
  bugIconSvg.setAttribute("viewBox", "0 0 32 32");
  bugIconSvg.style.display = "block";

  // Bug body (main oval)
  const body = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
  body.setAttribute("cx", "16");
  body.setAttribute("cy", "19");
  body.setAttribute("rx", "8");
  body.setAttribute("ry", "6");
  body.setAttribute("fill", "#ef4444");
  bugIconSvg.appendChild(body);

  // Head (smaller circle at top)
  const head = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  head.setAttribute("cx", "16");
  head.setAttribute("cy", "11");
  head.setAttribute("r", "5");
  head.setAttribute("fill", "#ef4444");
  bugIconSvg.appendChild(head);

  // Left wing
  const wingL = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
  wingL.setAttribute("cx", "10");
  wingL.setAttribute("cy", "18");
  wingL.setAttribute("rx", "4");
  wingL.setAttribute("ry", "3");
  wingL.setAttribute("fill", "#fca5a5");
  wingL.setAttribute("opacity", "0.6");
  bugIconSvg.appendChild(wingL);

  // Right wing
  const wingR = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
  wingR.setAttribute("cx", "22");
  wingR.setAttribute("cy", "18");
  wingR.setAttribute("rx", "4");
  wingR.setAttribute("ry", "3");
  wingR.setAttribute("fill", "#fca5a5");
  wingR.setAttribute("opacity", "0.6");
  bugIconSvg.appendChild(wingR);

  // Left antenna
  const antennaL = document.createElementNS("http://www.w3.org/2000/svg", "line");
  antennaL.setAttribute("x1", "13");
  antennaL.setAttribute("y1", "9");
  antennaL.setAttribute("x2", "8");
  antennaL.setAttribute("y2", "4");
  antennaL.setAttribute("stroke", "#ef4444");
  antennaL.setAttribute("stroke-width", "2");
  bugIconSvg.appendChild(antennaL);

  // Right antenna
  const antennaR = document.createElementNS("http://www.w3.org/2000/svg", "line");
  antennaR.setAttribute("x1", "19");
  antennaR.setAttribute("y1", "9");
  antennaR.setAttribute("x2", "24");
  antennaR.setAttribute("y2", "4");
  antennaR.setAttribute("stroke", "#ef4444");
  antennaR.setAttribute("stroke-width", "2");
  bugIconSvg.appendChild(antennaR);

  // Antenna tips
  const tipL = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  tipL.setAttribute("cx", "8");
  tipL.setAttribute("cy", "4");
  tipL.setAttribute("r", "1.5");
  tipL.setAttribute("fill", "#f59e0b");
  bugIconSvg.appendChild(tipL);

  const tipR = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  tipR.setAttribute("cx", "24");
  tipR.setAttribute("cy", "4");
  tipR.setAttribute("r", "1.5");
  tipR.setAttribute("fill", "#f59e0b");
  bugIconSvg.appendChild(tipR);

  // Body segments
  const segment1 = document.createElementNS("http://www.w3.org/2000/svg", "line");
  segment1.setAttribute("x1", "10");
  segment1.setAttribute("y1", "17");
  segment1.setAttribute("x2", "22");
  segment1.setAttribute("y2", "17");
  segment1.setAttribute("stroke", "#1e293b");
  segment1.setAttribute("stroke-width", "1.5");
  bugIconSvg.appendChild(segment1);

  const segment2 = document.createElementNS("http://www.w3.org/2000/svg", "line");
  segment2.setAttribute("x1", "10");
  segment2.setAttribute("y1", "21");
  segment2.setAttribute("x2", "22");
  segment2.setAttribute("y2", "21");
  segment2.setAttribute("stroke", "#1e293b");
  segment2.setAttribute("stroke-width", "1.5");
  bugIconSvg.appendChild(segment2);

  // Legs (left side)
  const legL1 = document.createElementNS("http://www.w3.org/2000/svg", "line");
  legL1.setAttribute("x1", "8");
  legL1.setAttribute("y1", "19");
  legL1.setAttribute("x2", "4");
  legL1.setAttribute("y2", "23");
  legL1.setAttribute("stroke", "#ef4444");
  legL1.setAttribute("stroke-width", "2");
  bugIconSvg.appendChild(legL1);

  const legL2 = document.createElementNS("http://www.w3.org/2000/svg", "line");
  legL2.setAttribute("x1", "9");
  legL2.setAttribute("y1", "22");
  legL2.setAttribute("x2", "5");
  legL2.setAttribute("y2", "26");
  legL2.setAttribute("stroke", "#ef4444");
  legL2.setAttribute("stroke-width", "2");
  bugIconSvg.appendChild(legL2);

  // Legs (right side)
  const legR1 = document.createElementNS("http://www.w3.org/2000/svg", "line");
  legR1.setAttribute("x1", "24");
  legR1.setAttribute("y1", "19");
  legR1.setAttribute("x2", "28");
  legR1.setAttribute("y2", "23");
  legR1.setAttribute("stroke", "#ef4444");
  legR1.setAttribute("stroke-width", "2");
  bugIconSvg.appendChild(legR1);

  const legR2 = document.createElementNS("http://www.w3.org/2000/svg", "line");
  legR2.setAttribute("x1", "23");
  legR2.setAttribute("y1", "22");
  legR2.setAttribute("x2", "27");
  legR2.setAttribute("y2", "26");
  legR2.setAttribute("stroke", "#ef4444");
  legR2.setAttribute("stroke-width", "2");
  bugIconSvg.appendChild(legR2);

  collapsedButton.appendChild(bugIconSvg);
  collapsedButton.setAttribute("aria-label", "Open debug console");
  collapsedButton.style.background = "rgba(8, 8, 8, 0.82)";
  collapsedButton.style.color = "#f0f0f0";
  collapsedButton.style.border = "1px solid rgba(255, 255, 255, 0.1)";
  collapsedButton.style.borderRadius = "999px";
  collapsedButton.style.width = "44px";
  collapsedButton.style.height = "44px";
  collapsedButton.style.display = collapsed ? "flex" : "none";
  collapsedButton.style.alignItems = "center";
  collapsedButton.style.justifyContent = "center";
  collapsedButton.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.35)";
  collapsedButton.style.cursor = "pointer";
  collapsedButton.style.touchAction = "manipulation";

  modalHeader.append(modalTitle, modalClose);
  modal.append(modalHeader, modalBody);
  modalBackdrop.append(modal);

  panel.append(header, logContainer);
  overlayRoot.append(collapsedButton, panel);
  document.body.appendChild(overlayRoot);
  document.body.appendChild(modalBackdrop);

  const formatValue = (value: unknown): string => {
    if (value instanceof Error) {
      return value.stack || `${value.name}: ${value.message}`;
    }

    if (typeof value === "object" && value !== null) {
      try {
        return JSON.stringify(value);
      } catch {
        return "[Unserializable Object]";
      }
    }

    return String(value);
  };

  const setModalVisible = (visible: boolean) => {
    modalBackdrop.style.display = visible ? "flex" : "none";
    if (!visible) {
      modalBody.replaceChildren();
    }
  };

  const renderBadge = (text: string, color: string) => {
    const badge = document.createElement("span");
    badge.textContent = text;
    badge.style.display = "inline-flex";
    badge.style.alignItems = "center";
    badge.style.justifyContent = "center";
    badge.style.padding = "2px 8px";
    badge.style.borderRadius = "999px";
    badge.style.fontSize = "11px";
    badge.style.fontWeight = "700";
    badge.style.background = color;
    badge.style.color = "#0a0a0a";
    return badge;
  };

  const formatSkillName = (skillId: string) =>
    SKILLS[skillId as keyof typeof SKILLS]?.name ?? skillId;

  const formatPerkName = (perkId: string) => PERKS[perkId as keyof typeof PERKS]?.name ?? perkId;

  const renderActionList = (title: string, actions: { turn: number; description: string }[]) => {
    const column = document.createElement("div");
    column.style.display = "flex";
    column.style.flexDirection = "column";
    column.style.gap = "4px";
    column.style.flex = "1";
    column.style.minWidth = "0";

    const heading = document.createElement("div");
    heading.textContent = title;
    heading.style.fontWeight = "600";
    heading.style.color = "#eceff1";
    column.append(heading);

    if (!actions.length) {
      const empty = document.createElement("div");
      empty.textContent = "No actions recorded.";
      empty.style.color = "#9e9e9e";
      empty.style.fontSize = "12px";
      column.append(empty);
      return column;
    }

    const list = document.createElement("ul");
    list.style.margin = "0";
    list.style.paddingLeft = "16px";
    list.style.color = "#cfd8dc";
    for (const action of actions) {
      const item = document.createElement("li");
      item.textContent = `T${action.turn}: ${action.description}`;
      item.style.marginBottom = "2px";
      list.append(item);
    }
    column.append(list);
    return column;
  };

  const renderEndingState = (encounter: MultiEncounterRun["floors"][number]["encounters"][number]) => {
    const container = document.createElement("div");
    container.style.display = "grid";
    container.style.gridTemplateColumns = "repeat(auto-fit, minmax(180px, 1fr))";
    container.style.gap = "6px";
    container.style.marginTop = "4px";

    const playerHealth = document.createElement("div");
    playerHealth.textContent = `Player HP: ${encounter.endingState.player.hp}/${encounter.endingState.player.maxHp}`;
    playerHealth.style.color = "#cfd8dc";
    container.append(playerHealth);

    const skills = document.createElement("div");
    const skillNames = Object.entries(encounter.endingState.player.skills).map(
      ([skillId, level]) => `${formatSkillName(skillId)} Lv${level}`
    );
    skills.textContent = `Skills: ${skillNames.length ? skillNames.join(", ") : "None"}`;
    skills.style.color = "#cfd8dc";
    container.append(skills);

    const perks = document.createElement("div");
    const perkNames = encounter.endingState.player.perks.map((perkId) => formatPerkName(perkId));
    perks.textContent = `Perks: ${perkNames.length ? perkNames.join(", ") : "None"}`;
    perks.style.color = "#cfd8dc";
    container.append(perks);

    const enemies = document.createElement("div");
    if (encounter.endingState.enemies.length === 0) {
      enemies.textContent = "Enemies: None recorded";
    } else {
      const enemyLines = encounter.endingState.enemies.map(
        (enemy) => `${enemy.name}: ${enemy.hp}/${enemy.maxHp} HP (ATK ${enemy.atk})`
      );
      enemies.textContent = `Enemies: ${enemyLines.join("; ")}`;
    }
    enemies.style.color = "#cfd8dc";
    container.append(enemies);

    return container;
  };

  const renderMultiEncounterRuns = (runs: MultiEncounterRun[]) => {
    const container = document.createElement("div");
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.gap = "10px";
    container.style.padding = "12px";
    container.style.background = "rgba(255, 255, 255, 0.02)";
    container.style.border = "1px solid rgba(255, 255, 255, 0.08)";
    container.style.borderRadius = "10px";

    const title = document.createElement("div");
    title.textContent = "Floor-by-floor behavior preview";
    title.style.fontWeight = "700";
    title.style.fontSize = "13px";
    container.append(title);

    if (!runs.length) {
      const empty = document.createElement("div");
      empty.textContent = "No multi-encounter runs available.";
      empty.style.color = "#9e9e9e";
      empty.style.fontSize = "12px";
      container.append(empty);
      return container;
    }

    runs.forEach((run, runIndex) => {
      const details = document.createElement("details");
      details.open = runIndex === 0;
      details.style.border = "1px solid rgba(255, 255, 255, 0.08)";
      details.style.borderRadius = "8px";
      details.style.background = "rgba(255, 255, 255, 0.02)";

      const summary = document.createElement("summary");
      summary.style.cursor = "pointer";
      summary.style.padding = "8px 10px";
      summary.style.display = "flex";
      summary.style.alignItems = "center";
      summary.style.gap = "8px";

      const runLabel = document.createElement("div");
      runLabel.style.display = "flex";
      runLabel.style.flexDirection = "column";
      runLabel.style.gap = "2px";
      const runTitle = document.createElement("span");
      runTitle.textContent = `Seed ${run.seed}`;
      runTitle.style.fontWeight = "600";
      const runSubtitle = document.createElement("span");
      runSubtitle.textContent = `Floors cleared: ${run.totalFloors}`;
      runSubtitle.style.color = "#b0bec5";
      runSubtitle.style.fontSize = "11px";
      runLabel.append(runTitle, runSubtitle);

      const outcomeBadge = renderBadge(
        run.finalOutcome === "completed" ? "COMPLETED" : "DEFEATED",
        run.finalOutcome === "completed" ? "#7ee081" : "#ff7961"
      );

      summary.append(runLabel, outcomeBadge);
      details.append(summary);

      const floorList = document.createElement("div");
      floorList.style.display = "flex";
      floorList.style.flexDirection = "column";
      floorList.style.gap = "8px";
      floorList.style.padding = "8px 10px 12px";

      run.floors.forEach((floor) => {
        const floorContainer = document.createElement("div");
        floorContainer.style.padding = "8px";
        floorContainer.style.border = "1px solid rgba(255, 255, 255, 0.08)";
        floorContainer.style.borderRadius = "8px";
        floorContainer.style.background = "rgba(0, 0, 0, 0.16)";
        floorContainer.style.display = "flex";
        floorContainer.style.flexDirection = "column";
        floorContainer.style.gap = "8px";

        const floorHeader = document.createElement("div");
        floorHeader.textContent = `Floor ${floor.floorNumber}: ${floor.floorTheme} (${floor.clearedEncounters}/${floor.totalEncounters} encounters)`;
        floorHeader.style.fontWeight = "600";
        floorHeader.style.color = "#eceff1";
        floorContainer.append(floorHeader);

        floor.encounters.forEach((encounter) => {
          const encounterBox = document.createElement("div");
          encounterBox.style.padding = "10px 12px";
          encounterBox.style.border = "1px solid rgba(255, 255, 255, 0.06)";
          encounterBox.style.borderRadius = "8px";
          encounterBox.style.background = "rgba(0, 0, 0, 0.24)";
          encounterBox.style.display = "flex";
          encounterBox.style.flexDirection = "column";
          encounterBox.style.gap = "8px";

          const headerRow = document.createElement("div");
          headerRow.style.display = "flex";
          headerRow.style.alignItems = "center";
          headerRow.style.gap = "8px";
          const encounterTitle = document.createElement("div");
          encounterTitle.textContent = `Encounter ${encounter.encounterNumber}`;
          encounterTitle.style.fontWeight = "600";
          const outcomeBadge = renderBadge(
            encounter.outcome.toUpperCase(),
            encounter.outcome === "win" ? "#7ee081" : encounter.outcome === "loss" ? "#ff7961" : "#ffc107"
          );
          const turns = document.createElement("span");
          turns.textContent = `${encounter.turns} turns`;
          turns.style.color = "#cfd8dc";
          turns.style.fontSize = "12px";
          headerRow.append(encounterTitle, outcomeBadge, turns);
          encounterBox.append(headerRow);

          const rewardRow = document.createElement("div");
          rewardRow.style.color = "#cfd8dc";
          rewardRow.style.fontSize = "12px";
          rewardRow.textContent = `Rewards: ${encounter.rewardsPicked.length ? encounter.rewardsPicked.join(", ") : "None"}`;
          encounterBox.append(rewardRow);

          const behaviorRow = document.createElement("div");
          behaviorRow.style.display = "flex";
          behaviorRow.style.flexWrap = "wrap";
          behaviorRow.style.gap = "10px";
          behaviorRow.append(
            renderActionList(
              "Player behavior",
              encounter.actions.filter((action) => action.actor === "player")
            ),
            renderActionList(
              "Enemy behavior",
              encounter.actions.filter((action) => action.actor === "enemy")
            )
          );
          encounterBox.append(behaviorRow);

          const endingState = document.createElement("div");
          endingState.style.display = "flex";
          endingState.style.flexDirection = "column";
          endingState.style.gap = "4px";
          const endingTitle = document.createElement("div");
          endingTitle.textContent = "End of encounter";
          endingTitle.style.fontWeight = "600";
          endingState.append(endingTitle, renderEndingState(encounter));
          encounterBox.append(endingState);

          floorContainer.append(encounterBox);
        });

        floorList.append(floorContainer);
      });

      details.append(floorList);
      container.append(details);
    });

    return container;
  };

  const renderBalanceContent = (multiRuns: MultiEncounterRun[]) => {
    return renderMultiEncounterRuns(multiRuns);
  };

  const formatEntry = (entry: LogEntry): HTMLElement => {
    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.gap = "8px";
    row.style.alignItems = "flex-start";
    row.style.marginTop = "4px";

    const label = document.createElement("span");
    label.textContent = entry.level.toUpperCase();
    label.style.flexShrink = "0";
    label.style.padding = "2px 6px";
    label.style.borderRadius = "4px";
    label.style.fontWeight = "700";
    label.style.fontSize = "11px";

    if (entry.level === "warn") {
      label.style.background = "rgba(255, 193, 7, 0.18)";
      label.style.color = "#ffc107";
    } else if (entry.level === "error") {
      label.style.background = "rgba(244, 67, 54, 0.22)";
      label.style.color = "#ff7961";
    } else {
      label.style.background = "rgba(96, 125, 139, 0.2)";
      label.style.color = "#b0bec5";
    }

    const message = document.createElement("span");
    message.textContent = entry.message;
    message.style.flex = "1";

    row.append(label, message);
    return row;
  };

  const appendLog = (level: LogLevel, args: unknown[]) => {
    // Auto-expand menu when errors are logged
    if (level === "error" && collapsed) {
      setCollapsed(false);
    }

    const shouldStickToBottom =
      logContainer.scrollHeight - (logContainer.scrollTop + logContainer.clientHeight) <=
      AUTO_SCROLL_THRESHOLD;

    const messageText = args.map(formatValue).join(" ");
    const entryElement = formatEntry({ level, message: messageText });
    entries.push(entryElement);
    logMessages.push(messageText);
    logContainer.appendChild(entryElement);

    if (entries.length > maxEntries) {
      const removed = entries.shift();
      removed?.remove();
      logMessages.shift();
    }

    if (shouldStickToBottom) {
      logContainer.scrollTop = logContainer.scrollHeight;
    }
  };

  const patchedConsole: Record<LogLevel, Console["log"]> = {
    log: (...args: unknown[]) => {
      if (!shouldSuppressLog(args)) {
        appendLog("log", args);
        originalConsole.log(...args);
      }
    },
    warn: (...args: unknown[]) => {
      if (!shouldSuppressLog(args)) {
        appendLog("warn", args);
        originalConsole.warn(...args);
      }
    },
    error: (...args: unknown[]) => {
      if (!shouldSuppressLog(args)) {
        appendLog("error", args);
        originalConsole.error(...args);
      }
    },
  };

  console.log = patchedConsole.log;
  console.warn = patchedConsole.warn;
  console.error = patchedConsole.error;

  const appendMappedError = async (
    prefix: string,
    errorValue: unknown,
    fallbackLocation?: string
  ) => {
    if (errorValue instanceof Error) {
      const mappedStack = await mapStackTrace(errorValue.stack);
      const errorBody =
        mappedStack || errorValue.stack || `${errorValue.name}: ${errorValue.message}`;
      appendLog("error", [[prefix, errorBody, fallbackLocation].filter(Boolean).join("\n")]);
      return;
    }

    appendLog("error", [
      [prefix, formatValue(errorValue), fallbackLocation].filter(Boolean).join("\n"),
    ]);
  };

  const handleError = (event: ErrorEvent) => {
    void appendMappedError(
      "Runtime error:",
      event.error ?? event.message,
      `${event.filename}:${event.lineno}:${event.colno}`
    );
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    void appendMappedError("Unhandled promise rejection:", event.reason);
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleRejection);

  const setCollapsed = (next: boolean) => {
    collapsed = next;
    logContainer.style.display = collapsed ? "none" : "block";
    panel.style.display = collapsed ? "none" : "flex";
    collapsedButton.style.display = collapsed ? "flex" : "none";
    overlayRoot.style.width = collapsed ? "auto" : "min(520px, 92vw)";
    toggleButton.textContent = collapsed ? "Expand" : "Collapse";
  };

  const clearLogs = () => {
    entries.splice(0).forEach((entry) => entry.remove());
    logMessages.splice(0);
  };

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
    } catch {
      // ignore copy errors
    } finally {
      textarea.remove();
    }
  };

  const copyText = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // fall back to execCommand copy
      }
    }
    fallbackCopy(text);
  };

  const copyLogs = async () => {
    if (!logMessages.length) {
      return;
    }
    await copyText(logMessages.join("\n"));
  };

  header.addEventListener("click", () => {
    setCollapsed(!collapsed);
  });

  toggleButton.addEventListener("click", (event) => {
    event.stopPropagation();
    setCollapsed(!collapsed);
  });

  clearButton.addEventListener("click", (event) => {
    event.stopPropagation();
    clearLogs();
  });

  copyButton.addEventListener("click", async (event) => {
    event.stopPropagation();
    const originalLabel = copyButton.textContent;
    copyButton.textContent = "Copied!";
    const prevBg = copyButton.style.background;
    const prevBorder = copyButton.style.border;
    copyButton.style.background = "rgba(126, 224, 129, 0.25)";
    copyButton.style.border = "1px solid rgba(126, 224, 129, 0.55)";
    copyButton.disabled = true;
    try {
      await copyLogs();
    } finally {
      setTimeout(() => {
        copyButton.textContent = originalLabel ?? "Copy";
        copyButton.style.background = prevBg;
        copyButton.style.border = prevBorder;
        copyButton.disabled = false;
      }, 900);
    }
  });

  balanceButton.addEventListener("click", (event) => {
    event.stopPropagation();
    try {
      const multiRuns = MULTI_ENCOUNTER_PRESET.seeds.map((seed) =>
        simulateMultiEncounterRun(
          MULTI_ENCOUNTER_PRESET.floors,
          deterministicPolicy,
          seed,
          MULTI_ENCOUNTER_PRESET.maxTurns
        )
      );
      modalBody.replaceChildren(renderBalanceContent(multiRuns));
      setModalVisible(true);
    } catch (error) {
      appendLog("error", ["Multi-encounter simulation failed", formatValue(error)]);
    }
  });

  modalClose.addEventListener("click", () => {
    setModalVisible(false);
  });

  modalBackdrop.addEventListener("click", (event) => {
    if (event.target === modalBackdrop) {
      setModalVisible(false);
    }
  });

  collapsedButton.addEventListener("click", () => {
    setCollapsed(false);
  });

  setCollapsed(collapsed);

  const overlay: DebugConsoleOverlay = {
    collapse: () => setCollapsed(true),
    expand: () => setCollapsed(false),
    toggle: () => setCollapsed(!collapsed),
    clear: clearLogs,
    destroy: () => {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;

      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);

      overlayRoot.remove();
      modalBackdrop.remove();
      entries.splice(0);
      activeOverlay = null;
    },
  };

  activeOverlay = overlay;
  return overlay;
}
